import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext'; // ✅ Auth context import
import './Navbar.css';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const { cartCount } = useCart();
  const { user, logout } = useAuth(); // ✅ Get user and logout from AuthContext
  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef(null);
  const searchRef = useRef(null);

  /* ── scroll listener ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── click-outside to close dropdowns ── */
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target) && searchOpen)
        setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen]);

  /* ── lock body scroll when drawer open ── */
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = encodeURIComponent(searchQuery.trim());
      navigate(`/shop?search=${query}`);
      setSearchQuery('');
      setSearchOpen(false);
      closeAll();
    }
  };

  const handleLogout = async () => {
    await logout();
    setProfileOpen(false);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path ? 'active' : '';
  const closeAll = () => { setIsMenuOpen(false); setSearchOpen(false); setProfileOpen(false); };

  /* ─────────────────────────────────────────────────
     SVG Icons (inline, no extra dep)
  ───────────────────────────────────────────────── */
  const IconSearch = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" />
    </svg>
  );
  const IconUser = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
  const IconHeart = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
  const IconBag = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
  const IconHome = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
  const IconMenu = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );

  /* ─── Logo markup (reused) ─── */
  const Logo = ({ onClick }) => (
    <Link to="/" className="logo" onClick={onClick}>
      <span className="logo-text">
        VK<span className="logo-highlight">TREND</span>
      </span>
    </Link>
  );

  return (
    <>
      {/* ══════════════════════════════════════════
          DESKTOP NAVBAR  (hidden on mobile)
      ══════════════════════════════════════════ */}
      <header className={`navbar desktop-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Logo onClick={closeAll} />

          <nav className="nav-links">
            <Link to="/" className={`nav-item ${isActive('/')}`}>Home</Link>

            <Link to="/newarrivals" className={`nav-item ${isActive('/newarrivals')}`}>New</Link>
           
            {/* <Link to="/women"        className={`nav-item ${isActive('/women')}`}>Women</Link> */}
            <Link to="/accessories" className={`nav-item ${isActive('/accessories')}`}>Accessories</Link>
            <Link to="/sale" className={`nav-item sale ${isActive('/sale')}`}>Sale</Link>
          </nav>

          <div className="nav-actions">
            {/* Search */}
            <div className="search-wrapper" ref={searchRef}>
              <form className="desktop-search-form" onSubmit={handleSearch}>
                <span className="desktop-search-icon"><IconSearch /></span>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="desktop-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear"
                  >
                    ✕
                  </button>
                )}
              </form>
            </div>

            {/* Profile */}
            <div className="profile-wrapper" ref={profileRef}>
              <button
                className="action-btn"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-label="Profile"
              >
                <IconUser />
              </button>
              <div className={`profile-dropdown ${profileOpen ? 'open' : ''}`}>
                {user ? (
                  <>
                    <div className="profile-header">
                      <div className="profile-avatar"><IconUser /></div>
                      <div>
                        <h4>{user.username}</h4>
                        <p>{user.email}</p>
                      </div>
                    </div>
                    <div className="dropdown-links">
                      <Link to="/profile" onClick={closeAll}>My Profile</Link>
                      <Link to="/orders" onClick={closeAll}>My Orders</Link>
                      <Link to="/wishlist" onClick={closeAll}>Wishlist</Link>
                      <Link to="/settings" onClick={closeAll}>Settings</Link>
                      <div className="divider" />
                      <button onClick={handleLogout} className="logout-btn">Sign Out</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="profile-header">
                      <div className="profile-avatar"><IconUser /></div>
                      <div>
                        <h4>Welcome!</h4>
                        <p>Sign in to account</p>
                      </div>
                    </div>
                    <div className="dropdown-links">
                      <Link to="/login" onClick={closeAll}>Sign In</Link>
                      <Link to="/register" onClick={closeAll}>Create Account</Link>
                      <div className="divider" />
                      <Link to="/wishlist" onClick={closeAll}>Wishlist</Link>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Wishlist */}
            <Link to="/wishlist" className="action-btn" aria-label="Wishlist">
              <IconHeart />
            </Link>

            {/* Cart */}
            <Link to="/cart" className="action-btn cart" aria-label="Cart">
              <IconBag />
              {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
            </Link>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════
          MOBILE TOP BAR  — logo + search bar
          (only visible on mobile)
      ══════════════════════════════════════════ */}
      <header className={`mobile-topbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="mobile-topbar-inner">
          <Logo onClick={closeAll} />

          <form className="mobile-search-form" onSubmit={handleSearch}>
            <span className="mobile-search-icon"><IconSearch /></span>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="mobile-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear"
              >
                ✕
              </button>
            )}
          </form>

          {/* Cart shortcut */}
          <Link to="/cart" className="action-btn cart mobile-cart-icon" aria-label="Cart">
            <IconBag />
            {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
          </Link>
        </div>
      </header>

      {/* ══════════════════════════════════════════
          MOBILE BOTTOM NAV BAR
          (only visible on mobile)
      ══════════════════════════════════════════ */}
      <nav className="mobile-bottom-nav" aria-label="Main navigation">
        <Link
          to="/"
          className={`bottom-nav-item ${isActive('/')}`}
          onClick={closeAll}
        >
          <IconHome />
          <span>Home</span>
        </Link>

        <Link
          to="/newarrivals"
          className={`bottom-nav-item ${isActive('/newarrivals')}`}
          onClick={closeAll}
        >
          {/* tag icon via SVG */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>New</span>
        </Link>

        <Link
          to="/sale"
          className={`bottom-nav-item sale-tab ${isActive('/sale')}`}
          onClick={closeAll}
        >
          {/* percent icon */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <line x1="19" y1="5" x2="5" y2="19" />
            <circle cx="6.5" cy="6.5" r="2.5" />
            <circle cx="17.5" cy="17.5" r="2.5" />
          </svg>
          <span>Sale</span>
        </Link>

        <Link
          to="/wishlist"
          className={`bottom-nav-item ${isActive('/wishlist')}`}
          onClick={closeAll}
        >
          <IconHeart />
          <span>Wishlist</span>
        </Link>

        <button
          className="bottom-nav-item"
          onClick={() => setIsMenuOpen(true)}
          aria-label="Open menu"
        >
          <IconMenu />
          <span>More</span>
        </button>
      </nav>

      {/* ══════════════════════════════════════════
          MOBILE SLIDE DRAWER  (More menu)
      ══════════════════════════════════════════ */}
      <div
        className={`mobile-overlay ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
      />
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <Logo onClick={closeAll} />
          <button className="close-btn" onClick={() => setIsMenuOpen(false)} aria-label="Close">✕</button>
        </div>

        <div className="mobile-menu-body">
          <Link to="/" onClick={closeAll}>Home</Link>
          <Link to="/newarrivals" onClick={closeAll}>New Arrivals</Link>
          
          
          <Link to="/accessories" onClick={closeAll}>Accessories</Link>
          <Link to="/sale" onClick={closeAll} className="sale-link">Sale 🔥</Link>
          <Link to="/account" onClick={closeAll}>My Account</Link>
          <Link to="/orders" onClick={closeAll}>My Orders</Link>
          <Link to="/settings " onClick={closeAll}>Settings</Link>
        </div>

        <div className="mobile-menu-footer">
          {user ? (
            <div className="user-info">
              <div className="user-avatar"><IconUser /></div>
              <div>
                <p>{user.username}</p>
                <span>{user.email}</span>
              </div>
              <button onClick={handleLogout} className="logout-btn">Sign Out</button>
            </div>
          ) : (
            <div className="user-info">
              <div className="user-avatar"><IconUser /></div>
              <div>
                <p>Guest User</p>
                <span>Sign in for better experience</span>
              </div>
              <Link to="/login" onClick={closeAll} className="login-link">Sign In</Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Navbar;